import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import MarketDealModal from '../components/MarketDealModal';
import SoldCloseModal from '../components/SoldCloseModal';
import MarkAvailableModal from '../components/MarkAvailableModal';
import { EXEMPT_SOURCES } from '../components/StageBadge';
import StageBadge from '../components/StageBadge';
import DealsMap from '../components/DealsMap';
import { proxyPhoto } from '../utils/photo';
import { useLists, RowCheckbox, SelectionBar, ListFilter, ListDots } from '../components/Lists';
import { useAuth } from '../contexts/AuthContext';

const VIEW_KEY = 'feebe_deals_view';

// TEMPORARY bridge (Tim Lange / org 57): his Available deals are mirrored from a Google Sheet. On his
// Available tab we show the sheet's own columns (Address/Price/Summary/ARV/COE/Picture Link) and a
// "Sync now" button + a review banner for deals that left the sheet. Gated to this one org; every
// other org keeps the standard pipeline columns. Matches SHEET_SYNC_ORG_ID in backend sheet-sync.js.
const SHEET_SYNC_ORG_ID = 57;

// Per-user working list. The stage tabs below are scoped to the logged-in agent's own deals
// (owner_user_id) by the backend — /api/deals and /api/deals/counts filter on req.userId. The
// shared, org-wide metric boxes live on the separate "Pipeline" page (Dashboard.jsx).
//
// Pipeline stage flow (Step 3 target model):
//   Available → Working → Offer Sent → Under Contract → Offer Accepted → Sold → Closed
//   (Dropped + Dead are terminal off-ramps; Past is the Salesforce historical reference)
// Keep this in sync with backend/services/deal-stages.js and the TAB_FILTERS in deals.js.
// Bucket order mirrors the client's workflow: Tagged Deals (the general working pool) → Offer Sent →
// Under Contract → Available → Sold → Closed → Dropped → Dead. "Tagged Deals" is the 'working' stage
// relabeled. Offer Accepted + Past are retained at the end so no existing functionality is lost.
// Keep stage keys in sync with backend/services/deal-stages.js and TAB_FILTERS in deals.js.
// Table column key -> the sort key the API allows (routes/deals.js SORT_COLUMNS). A column absent
// from this map simply isn't sortable — the checkbox, photo, notes and action columns have nothing
// to sort by. Keeping the mapping here means a header can never ask for a column the API refuses.
const SORTABLE = {
  addr: 'address', yearbuilt: 'year_built', list: 'asking_price', arv: 'ai_arv', offer: 'offer_amount',
  agent: 'agent_name', offerdt: 'offer_date', liststat: 'listing_status',
  offstat: 'offer_status', closed: 'close_date', title: 'title_company',
};

const PIPELINE_TABS = [
  { key: 'tagged',         label: 'Tagged Deals',   desc: 'Deals you are working — contacted, no contract yet' },
  { key: 'offer-sent',     label: 'Offer Sent',     desc: 'Offer / purchase contract sent' },
  { key: 'under-contract', label: 'Under Contract', desc: 'Seller signed — executed contract on file' },
  { key: 'available',      label: 'Available',      desc: 'Ready to assign — surfaced in the shared Pipeline' },
  { key: 'sold',           label: 'Sold',           desc: 'Buyer accepted — close pending' },
  { key: 'closed',         label: 'Closed',         desc: 'Escrow closed, deal complete' },
  { key: 'dropped',        label: 'Dropped',        desc: 'Fell through / passed' },
  { key: 'dead',           label: 'Dead',           desc: 'Off the table' },
];

// Bucket tab key → canonical stage (for drag-and-drop between buckets).
const TAB_STAGE = {
  'tagged': 'Tagged', 'offer-sent': 'Offer Sent', 'under-contract': 'Under Contract',
  'available': 'Available', 'sold': 'Sold', 'closed': 'Closed', 'dropped': 'Dropped', 'dead': 'Dead',
};

// Offer-status options for the inline dropdown in the list view.
export const OFFER_STATUS_OPTIONS = ['Awaiting response', 'Countered', 'Accepted', 'Rejected'];
// Common MLS/market statuses for the inline listing-status dropdown.
export const LISTING_STATUS_OPTIONS = ['Active', 'Active Under Contract', 'Pending', 'Back on Market', 'Withdrawn', 'Expired', 'Off-Market', 'Sold'];

// Stage pill + dropdown live in components/StageBadge.jsx (shared with Underwriting + PipelineDeal).

export function fmtMoney(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000)    return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
}

export function fmtDate(d) {
  if (!d) return '—';
  const s = String(d).split('T')[0].split(' ')[0];
  if (!s || s === 'null') return '—';
  const [y, m, day] = s.split('-');
  return (y && m && day) ? `${m}/${day}/${y}` : s;
}


// Parse a money input string → integer dollars (strips $ , spaces; supports k/m suffix). '' → null.
function parseMoneyInput(raw) {
  if (raw == null) return null;
  let m = String(raw).trim().toLowerCase().replace(/[$,\s]/g, '');
  if (!m) return null;
  let mult = 1;
  if (m.endsWith('k')) { mult = 1e3; m = m.slice(0, -1); }
  else if (m.endsWith('m')) { mult = 1e6; m = m.slice(0, -1); }
  const n = Number(m);
  return Number.isFinite(n) ? Math.round(n * mult) : null;
}

// Estimated-vs-verified styling: 'manual' source → bold + full color (user verified);
// anything else (app estimate / null) → muted grey + translucent.
function metricStyle(source, verifiedColor) {
  return source === 'manual'
    ? { color: verifiedColor || 'var(--white)', fontWeight: 700, opacity: 1 }
    : { color: 'var(--gray-500)', fontWeight: 500, opacity: 0.75 };
}

// Inline-editable money cell. Click to edit; Enter/blur saves, Esc cancels. When empty, shows `hint`.
export function InlineMoney({ value, onSave, source, verifiedColor, hint, tag }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const start = () => { setDraft(value != null ? String(value) : ''); setEditing(true); };
  const commit = () => {
    const parsed = parseMoneyInput(draft);
    setEditing(false);
    if (parsed !== (value ?? null)) onSave(parsed);
  };
  if (editing) {
    return (
      <input autoFocus type="text" value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        placeholder="$"
        style={{ width: 90, padding: '4px 6px', fontSize: 13, borderRadius: 6, border: '1px solid var(--teal)', background: 'var(--navy)', color: 'var(--white)' }} />
    );
  }
  const has = value != null && value !== '';
  return (
    <button onClick={start} title="Click to edit"
      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: 5, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5, ...(has ? metricStyle(source, verifiedColor) : { color: 'var(--gray-500)', fontWeight: 500, opacity: 0.7 }) }}>
      {has ? fmtMoney(value) : <span style={{ fontStyle: 'italic' }}>{hint || '—'}</span>}
      {tag && has && <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.06em', color: 'var(--teal)', border: '1px solid rgba(20,80,74,0.4)', borderRadius: 4, padding: '1px 3px' }}>{tag}</span>}
      <PencilTiny />
    </button>
  );
}

// Inline year-built cell. Editable rather than read-only because year built is one of the fields a
// deal MUST carry before it can be published to the shared board (lib/availableRequirements.js), and
// sheet-sourced rows routinely arrive without it — so the column an agent scans is also the fastest
// place to fill it in. A 4-digit sanity bound keeps a mistyped "202" out of the contract.
export function InlineYear({ value, onSave }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const start = () => { setDraft(value != null ? String(value) : ''); setEditing(true); };
  const commit = () => {
    setEditing(false);
    const t = draft.trim();
    const n = t === '' ? null : parseInt(t, 10);
    // Out of range is treated as "no change", not as a clear: silently blanking a year the agent
    // fat-fingered would lose a value they already had.
    if (n != null && (!Number.isFinite(n) || n < 1800 || n > new Date().getFullYear() + 2)) return;
    if (n !== (value ?? null)) onSave(n);
  };
  if (editing) {
    return (
      <input autoFocus type="text" inputMode="numeric" value={draft}
        onChange={e => setDraft(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        placeholder="1985"
        style={{ width: 58, padding: '4px 6px', fontSize: 13, borderRadius: 6, border: '1px solid var(--teal)', background: 'var(--navy)', color: 'var(--white)' }} />
    );
  }
  return (
    <button onClick={start} title="Click to edit"
      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: 5, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5, color: value ? 'var(--gray-300)' : 'var(--gray-500)', fontWeight: value ? 600 : 500, opacity: value ? 1 : 0.7 }}>
      {value || <span style={{ fontStyle: 'italic' }}>—</span>}
      <PencilTiny />
    </button>
  );
}

// Inline date cell (native picker). Saves on change. Value is an ISO date (YYYY-MM-DD) or null.
export function InlineDate({ value, onSave }) {
  const iso = value ? String(value).split('T')[0].split(' ')[0] : '';
  return (
    <input type="date" value={iso}
      onChange={e => onSave(e.target.value || null)}
      style={{ width: 128, boxSizing: 'border-box', padding: '4px 6px', fontSize: 11, borderRadius: 6, border: '1px solid var(--navy-border)', background: 'transparent', color: value ? 'var(--gray-300)' : 'var(--gray-500)', cursor: 'pointer' }} />
  );
}

// Colors for the offer/listing status dropdowns.
const OFFER_STATUS_COLOR = { 'Accepted': '#4A7C59', 'Rejected': '#B85450', 'Countered': '#E0A526', 'Awaiting response': '#9C9690' };
const LISTING_STATUS_COLOR = { 'Active': '#4A7C59', 'Pending': '#E0A526', 'Active Under Contract': '#E0A526', 'Back on Market': '#4A7C59', 'Withdrawn': '#B85450', 'Expired': '#B85450', 'Off-Market': '#9C9690', 'Sold': '#9C9690' };

// Inline status dropdown. Saves on change; '' → null.
export function InlineSelect({ value, options, onSave, placeholder, tone }) {
  const map = tone === 'offer' ? OFFER_STATUS_COLOR : LISTING_STATUS_COLOR;
  const color = (value && map[value]) || 'var(--gray-300)';
  return (
    <select value={value || ''} onChange={e => onSave(e.target.value || null)}
      style={{ padding: '4px 6px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: '1px solid var(--navy-border)', background: 'transparent', color, cursor: 'pointer', maxWidth: 128 }}>
      <option value="">{placeholder || '—'}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

// Listing status. For MLS-linked deals it auto-reflects the LIVE MLS status (read-only, from the
// backend's mls_listings join) — no manual entry needed. For off-market / manual deals it's an
// editable dropdown.
export function ListingStatusCell({ deal, onSave }) {
  if (deal.mls_status) {
    const color = LISTING_STATUS_COLOR[deal.mls_status] || 'var(--gray-300)';
    return (
      <span title="Live status from the MLS — updates automatically" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color }}>{deal.mls_status}</span>
        <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.06em', color: 'var(--gray-500)', border: '1px solid var(--navy-border)', borderRadius: 4, padding: '1px 3px' }}>MLS</span>
      </span>
    );
  }
  return <InlineSelect value={deal.listing_status} options={LISTING_STATUS_OPTIONS} onSave={onSave} placeholder="—" tone="listing" />;
}

// Pin-to-top star. Gold when pinned.
function PinStar({ pinned, onToggle }) {
  return (
    <button onClick={onToggle} title={pinned ? 'Unpin' : 'Pin to top'}
      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 0, color: pinned ? 'var(--gold)' : 'var(--gray-500)' }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill={pinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l2.9 6.3 6.6.6-5 4.4 1.5 6.5L12 17l-5.9 3.3 1.5-6.5-5-4.4 6.6-.6z"/>
      </svg>
    </button>
  );
}

// Days between two ISO/date strings at day granularity (b - a), or null.
function dayDiff(a, b) {
  if (!a || !b) return null;
  const da = new Date(String(a).slice(0, 10)); const db = new Date(String(b).slice(0, 10));
  if (isNaN(da) || isNaN(db)) return null;
  return Math.round((db - da) / 86400000);
}

// Follow-up badge: shows the next scheduled follow-up (with urgency color); if none is scheduled,
// shows when the deal was last followed up. Empty when neither exists.
export function FollowUpBadge({ next, last }) {
  const todayIso = new Date().toISOString().slice(0, 10);
  if (next) {
    const d = dayDiff(todayIso, next);
    let label, color;
    if (d == null)      { label = 'Scheduled'; color = '#9C9690'; }
    else if (d < 0)     { label = `${-d} day${-d > 1 ? 's' : ''} late`; color = '#B85450'; }
    else if (d === 0)   { label = 'Today'; color = '#E0A526'; }
    else if (d === 1)   { label = 'Tomorrow'; color = '#E0A526'; }
    else                { label = `in ${d} days`; color = '#5B8DB8'; }
    return <Pill label={label} color={color} title={`Next follow-up: ${fmtDate(next)}`} />;
  }
  if (last) return <Pill label="Followed up" color="#4A7C59" title={`Last followed up: ${fmtDate(last)}`} />;
  return <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>No follow-up</span>;
}

function Pill({ label, color, title }) {
  return (
    <span title={title} style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 12, fontSize: 11, fontWeight: 700, color, background: `${color}1F`, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

// Anchors a fixed-position panel to a trigger element, flipping above when there isn't room below,
// and tracking scroll/resize. Returns the computed rect (or null when closed).
function useAnchorRect(open, anchorRef, width) {
  const [rect, setRect] = React.useState(null);
  React.useLayoutEffect(() => {
    if (!open || !anchorRef.current) { setRect(null); return; }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth, vh = window.innerHeight;
      let left = Math.max(8, Math.min(r.right - width, vw - width - 8));
      const below = vh - r.bottom;
      const flipUp = below < 300 && r.top > below;
      setRect({ left, top: r.bottom + 6, bottom: vh - r.top + 6, flipUp, width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [open, anchorRef, width]);
  return rect;
}

// Renders its children into document.body (via portal) at the anchored rect — escaping any
// overflow:hidden / scroll-container clipping (the reason the old popovers were cut off).
function PortalPanel({ rect, panelRef, children, pad = 0 }) {
  if (!rect) return null;
  return createPortal(
    <div ref={panelRef} style={{
      position: 'fixed', left: rect.left, width: rect.width, zIndex: 9999,
      ...(rect.flipUp ? { bottom: rect.bottom } : { top: rect.top }),
      background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10,
      boxShadow: '0 12px 34px rgba(26,23,20,0.22)', padding: pad,
    }}>
      {children}
    </div>,
    document.body,
  );
}

// Per-deal notes popover. Fetches the thread on open; add a note (Enter to submit). Each note logs
// the author + timestamp server-side. Badge shows the note count. Rendered via portal so it is never
// clipped by the table's scroll container or the card's overflow:hidden.
function NotesPopover({ dealId, count }) {
  const [open, setOpen] = React.useState(false);
  const [notes, setNotes] = React.useState(null);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [localCount, setLocalCount] = React.useState(count);
  const btnRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const rect = useAnchorRect(open, btnRef, 320);

  React.useEffect(() => {
    if (!open) return;
    setNotes(null);
    fetch(`/api/deals/${dealId}/notes`).then(r => r.json())
      .then(d => setNotes(Array.isArray(d) ? d : [])).catch(() => setNotes([]));
    function onDoc(e) {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, dealId]);

  const add = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/deals/${dealId}/notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body }),
      });
      const note = await r.json();
      if (!r.ok || note.error) throw new Error(note.error || 'failed');
      setNotes(prev => [...(prev || []), note]);
      setLocalCount(c => c + 1);
      setDraft('');
    } catch (e) { alert(`Add note failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  const fmtStamp = (s) => {
    const d = new Date(s);
    return isNaN(d) ? String(s) : d.toLocaleString([], { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <>
      <button ref={btnRef} onClick={() => setOpen(o => !o)} title="Notes"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, position: 'relative', color: localCount > 0 ? 'var(--teal)' : 'var(--gray-500)', display: 'inline-flex' }}>
        <NoteIcon />
        {localCount > 0 && (
          <span style={{ position: 'absolute', top: -2, right: -2, minWidth: 14, height: 14, padding: '0 3px', borderRadius: 7, background: 'var(--teal)', color: '#fff', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{localCount}</span>
        )}
      </button>
      {open && (
        <PortalPanel rect={rect} panelRef={panelRef} pad={12}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Notes</div>
          <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
            {notes == null ? <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading…</div>
              : notes.length === 0 ? <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>No notes yet.</div>
              : notes.map(n => (
                <div key={n.id} style={{ borderLeft: '2px solid var(--navy-border)', paddingLeft: 8 }}>
                  <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{fmtStamp(n.created_at)}{n.author ? ` · ${n.author}` : ''}</div>
                  <div style={{ fontSize: 12, color: 'var(--gray-200)', whiteSpace: 'pre-wrap' }}>{n.body}</div>
                </div>
              ))}
          </div>
          <textarea value={draft} onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); add(); } }}
            placeholder="Add a note — call notes, next steps… (Enter to add, Shift+Enter for a line break)"
            rows={2}
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 12, borderRadius: 6, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', resize: 'vertical' }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
            <button onClick={add} disabled={busy || !draft.trim()}
              style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: busy || !draft.trim() ? 'default' : 'pointer', opacity: busy || !draft.trim() ? 0.5 : 1 }}>
              {busy ? 'Adding…' : 'Add note'}
            </button>
          </div>
        </PortalPanel>
      )}
    </>
  );
}

function PencilTiny() { return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>; }
function NoteIcon() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>; }

export default function Deals() {
  const [activeTab, setActiveTab] = useState('tagged');
  const [deals, setDeals]         = useState([]);
  const [counts, setCounts]       = useState({});
  const [loading, setLoading]     = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [listFilter, setListFilter]   = useState(null);
  // List / Map / Detail view (persisted). selectedDealId drives the map highlight + detail pane.
  const [viewMode, setViewMode]       = useState(() => localStorage.getItem(VIEW_KEY) || 'list');
  const [selectedDealId, setSelectedDealId] = useState(null);
  const setView = useCallback((v) => { setViewMode(v); try { localStorage.setItem(VIEW_KEY, v); } catch {} }, []);
  // Deal currently open in the Market wizard (null = wizard closed)
  const [marketDeal, setMarketDeal]   = useState(null);
  // Deal being marked Sold via the one-click Available → Sold shortcut (null = closed)
  const [sellDeal, setSellDeal]       = useState(null);
  // Drag-and-drop: id of the row being dragged + the bucket tab currently hovered as a drop target.
  const [draggingId, setDraggingId]   = useState(null);
  const [dragOverTab, setDragOverTab] = useState(null);
  // Sold/Closed drops route through the price+close-date prompt: { deal, stage }.
  const [stageModal, setStageModal]   = useState(null);
  const [publishDeal, setPublishDeal] = useState(null);   // deal whose required-fields form is open
  const { memberSet } = useLists();
  const { org, role } = useAuth();
  const navigate = useNavigate();

  // Sheet-bridge (org 57): the Available tab mirrors Tim's Google Sheet. These gate the sheet-style
  // columns, the "Sync now" button, and the "left the sheet" review banner — only for this org+tab.
  const isSheetSyncOrg = org?.id === SHEET_SYNC_ORG_ID;
  // Manual sheet sync is an ORG-level operation that spends the org's API budget — owner/admin only
  // (mirrors the server gate in routes/deals.js POST /sync-sheet).
  const canSheetSync = role === 'owner' || role === 'admin';
  const sheetView = isSheetSyncOrg && activeTab === 'available';
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  // Mine ⇄ Everyone. Default 'mine' for EVERYONE including the org owner: an owner works their own
  // deals alongside their agents, so their home view is the same personal workspace. 'all' is the
  // manager view and the backend only honours it for an owner/admin (routes/deals.js ownerScope).
  const [scope, setScope] = useState('mine');

  // Period. Defaults to ALL TIME, unlike the Pipeline's current-month: an agent opening their own
  // book should see everything they have, not an empty screen because they haven't offered on
  // anything yet this month. Filtering is on the OFFER date (lib/periods DEALS_EFF_DATE), falling
  // back to the close date — the Pipeline files by when money landed, this by when the offer went out.
  const [period, setPeriod] = useState('all');

  // Column sort. null = the backend's default (newest offer first). Clicking a header cycles
  // desc -> asc -> off, so a user can always get back to the default without reloading.
  const [sort, setSort] = useState(null);          // { key, dir }
  const onSort = (key) => setSort((cur) => {
    if (!cur || cur.key !== key) return { key, dir: 'desc' };
    if (cur.dir === 'desc') return { key, dir: 'asc' };
    return null;
  });

  const qs = `${scope === 'all' ? '&scope=all' : ''}&period=${period}`
    + (sort ? `&sort=${sort.key}&dir=${sort.dir}` : '');

  // Dashboard summary — the four buckets the agents think in, for the chosen period.
  const [summary, setSummary] = useState(null);

  // Count + summary refetcher — both scoped to match the list exactly, or the badges and cards would
  // advertise rows the table can't show. Stage-change handlers call this.
  const refreshCounts = useCallback(() => {
    fetch(`/api/deals/counts?_=1${qs}`).then(r => r.json()).then(setCounts).catch(() => {});
    fetch(`/api/deals/dashboard?_=1${qs}`).then(r => r.json()).then(setSummary).catch(() => {});
  }, [qs]);
  useEffect(() => { refreshCounts(); }, [refreshCounts]);

  const fetchDeals = useCallback((tab) => {
    setLoading(true);
    fetch(`/api/deals?tab=${tab}${qs}`)
      .then(r => r.json())
      // Keep deals an array even if the body isn't one (defensive — never feed .filter/.map an error body).
      .then(data => { setDeals(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [qs]);

  useEffect(() => { fetchDeals(activeTab); setSelectedIds(new Set()); }, [activeTab, fetchDeals]);

  // Org members for the owner-reassignment dropdown (just the owner today; scales as agents join).
  const [orgUsers, setOrgUsers] = useState([]);
  useEffect(() => {
    fetch('/api/deals/org-users').then(r => r.json())
      .then(d => setOrgUsers(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const reload = useCallback(() => { fetchDeals(activeTab); refreshCounts(); }, [fetchDeals, activeTab, refreshCounts]);

  // Sheet-bridge (org 57): pull the Google Sheet on demand, then reload the list.
  const doSheetSync = useCallback(async () => {
    setSyncing(true); setSyncMsg(null);
    try {
      const r = await fetch('/api/deals/sync-sheet', { method: 'POST' });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      const bits = [];
      if (d.inserted) bits.push(`${d.inserted} new`);
      if (d.updated) bits.push(`${d.updated} updated`);
      if (d.stage_changes) bits.push(`${d.stage_changes} moved to Sold / Closed / Dropped`);
      setSyncMsg(bits.length ? `Synced — ${bits.join(', ')}.` : 'Synced — no changes.');
      reload();
    } catch (e) { setSyncMsg(`Sync failed: ${e.message}`); }
    finally { setSyncing(false); }
  }, [reload]);

  // Sheet-bridge (org 57): resolve a "left the sheet" deal as Dropped (Sold goes through SoldCloseModal).
  const handleMarkDropped = useCallback(async (id) => {
    try {
      const r = await fetch(`/api/deals/${id}/stage`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: 'Dropped' }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      reload();
    } catch (e) { alert(`Failed: ${e.message}`); }
  }, [reload]);

  // Delete one deal — confirm (count = 1), hard-delete, no outbound. Backend is org-scoped via RLS.
  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this deal? This permanently removes the deal and its history, and cannot be undone.')) return;
    try {
      const r = await fetch(`/api/deals/${id}`, { method: 'DELETE' });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      reload();
    } catch (e) { alert(`Delete failed: ${e.message}`); }
  }, [reload]);

  // Mass delete the checked rows — confirm shows the exact count being deleted.
  const handleBulkDelete = useCallback(async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} deal${ids.length > 1 ? 's' : ''}? This permanently removes ${ids.length > 1 ? 'them' : 'it'} and all history, and cannot be undone.`)) return;
    try {
      const r = await fetch('/api/deals/bulk-delete', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      setSelectedIds(new Set());
      reload();
      if (d.deleted < ids.length) alert(`Deleted ${d.deleted} of ${ids.length}. ${ids.length - d.deleted} were not deletable.`);
    } catch (e) { alert(`Bulk delete failed: ${e.message}`); }
  }, [selectedIds, reload]);

  // Mark a deal Available — surfaces it in the shared org-wide Pipeline (Active Deals box).
  // The board is what the whole team sells from, so the deal has to be complete first: this opens
  // the required-fields form, which collects what's missing and makes the stage change itself.
  // Bridge- and import-sourced rows publish on their own terms and move straight through, matching
  // the server's own exemption (backend/lib/availableRequirements.js). Fires no outbound either way.
  const handleMarkAvailable = useCallback(async (id) => {
    const deal = deals.find(d => d.id === id);
    if (deal && !EXEMPT_SOURCES.has(deal.deal_source)) { setPublishDeal(deal); return; }
    try {
      const r = await fetch(`/api/deals/${id}/stage`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stage: 'Available' }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      reload();
    } catch (e) { alert(`Failed to mark available: ${e.message}`); }
  }, [deals, reload]);

  // Patch one deal in local state (optimistic UI for inline edits / pin).
  const patchDeal = useCallback((id, patch) => {
    setDeals(prev => prev.map(d => (d.id === id ? { ...d, ...patch } : d)));
  }, []);

  // Inline edit from the list view (ARV, offer amount, offer date, offer status, listing status,
  // agent fields). Optimistically patches local state, then PUTs /api/deals/:id. On a manual metric
  // edit the backend flips the matching *_source flag to 'manual'; mirror that here so the value
  // immediately renders bold-black (verified) without a refetch.
  const handleInlineSave = useCallback(async (id, patch) => {
    const localPatch = { ...patch };
    if ('ai_arv' in patch)      localPatch.arv_source = 'manual';
    if ('max_offer' in patch)   localPatch.mao_source = 'manual';
    if ('repair_cost' in patch) localPatch.repair_source = 'manual';
    patchDeal(id, localPatch);
    try {
      const r = await fetch(`/api/deals/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
    } catch (e) { alert(`Save failed: ${e.message}`); reload(); }
  }, [patchDeal, reload]);

  // Pin / unpin a deal — floats it to the top of the list. Reload to re-sort (pinned first).
  const handlePin = useCallback(async (id, pinned) => {
    patchDeal(id, { pinned: pinned ? 1 : 0 });
    try {
      const r = await fetch(`/api/deals/${id}/pin`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pinned }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      reload();
    } catch (e) { alert(`Pin failed: ${e.message}`); reload(); }
  }, [patchDeal, reload]);

  // Reassign a deal's owner to another member of the org.
  const handleReassign = useCallback(async (id, ownerUserId) => {
    try {
      const r = await fetch(`/api/deals/${id}/owner`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ owner_user_id: ownerUserId }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      reload(); // reassigning to another agent removes it from my (owner-scoped) list
    } catch (e) { alert(`Reassign failed: ${e.message}`); }
  }, [reload]);

  // Drop a dragged deal onto a bucket tab → change its stage (same chokepoint as the stage badge).
  // Sold/Closed route through the price+close-date prompt; a drop onto the deal's own bucket is a no-op.
  const handleDropOnTab = useCallback(async (tabKey) => {
    const id = draggingId;
    setDraggingId(null); setDragOverTab(null);
    if (id == null) return;
    const stage = TAB_STAGE[tabKey];
    const deal = deals.find(d => d.id === id);
    if (!stage || !deal || deal.stage === stage) return;
    if (stage === 'Sold' || stage === 'Closed') { setStageModal({ deal, stage }); return; }
    // Dropping onto the Available tab is publishing — same required-fields gate as the row action.
    if (stage === 'Available' && !EXEMPT_SOURCES.has(deal.deal_source)) { setPublishDeal(deal); return; }
    try {
      const r = await fetch(`/api/deals/${id}/stage`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stage }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      reload();
    } catch (e) { alert(`Move failed: ${e.message}`); reload(); }
  }, [draggingId, deals, reload]);

  const isClosedTab = activeTab === 'closed';


  const visibleDeals = useMemo(() => (
    listFilter ? deals.filter(d => memberSet(listFilter).has(`deal:${d.id}`)) : deals
  ), [deals, listFilter, memberSet]);

  // Sheet-bridge (org 57): deals flagged as no longer in the sheet, awaiting Sold/Dropped triage.
  const flaggedDeals = useMemo(() => (
    sheetView ? visibleDeals.filter(d => d.sheet_removed_at) : []
  ), [sheetView, visibleDeals]);

  // In the detail view, keep a valid selection: default to the first visible deal, and re-point if the
  // current selection dropped out of the visible set (tab switch, filter change).
  useEffect(() => {
    if (viewMode !== 'detail' || !visibleDeals.length) return;
    if (!selectedDealId || !visibleDeals.some(d => d.id === selectedDealId)) {
      setSelectedDealId(visibleDeals[0].id);
    }
  }, [viewMode, visibleDeals, selectedDealId]);

  const toggleSelect = (id, on) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });
  const allVisibleSelected = visibleDeals.length > 0 && visibleDeals.every(d => selectedIds.has(d.id));
  const toggleSelectAll = () => setSelectedIds(prev => {
    if (allVisibleSelected) return new Set();
    return new Set(visibleDeals.map(d => d.id));
  });
  const selectionItems = [...selectedIds].map(id => ({ item_type: 'deal', item_id: String(id) }));

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.02em', marginBottom: 6 }}>Deals</h1>
          <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Your personal working list — the deals you own.</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
          <button onClick={() => navigate('/deals/new')}
            style={{ padding: '9px 16px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Add Deal
          </button>
          <button onClick={() => navigate('/import')}
            style={{ padding: '9px 16px', borderRadius: 8, background: 'var(--teal)', border: '1px solid var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            ↓ Import Deals
          </button>
        </div>
      </div>

      {/* Deal dashboard — what this agent has in flight for the chosen period. Cards are clickable
          and jump to the matching bucket, so the summary is a way into the work, not just a readout. */}
      <DealDashboard
        summary={summary} period={period} onPeriod={setPeriod}
        activeTab={activeTab} onPick={setActiveTab}
        scope={scope} onScope={setScope} canScope={!!counts.can_view_org_wide}
      />

      {/* Card panel */}
      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, overflow: 'hidden' }}>
        {/* Tab bar */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--navy-border)', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {PIPELINE_TABS.map(t => {
            const active = activeTab === t.key;
            const count = counts[t.key];
            // Is a drag in progress toward a *different* bucket than the dragged deal's own?
            const isDropTarget = draggingId != null && dragOverTab === t.key;
            return (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                title={draggingId != null ? `Drop here to move to ${TAB_STAGE[t.key] || t.label}` : t.desc}
                onDragOver={draggingId != null ? (e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverTab !== t.key) setDragOverTab(t.key); }) : undefined}
                onDragLeave={() => { if (dragOverTab === t.key) setDragOverTab(null); }}
                onDrop={draggingId != null ? (e => { e.preventDefault(); handleDropOnTab(t.key); }) : undefined}
                style={{
                  padding: '5px 14px', borderRadius: 20, cursor: 'pointer',
                  fontSize: 12, fontWeight: 500,
                  border: isDropTarget ? '2px dashed var(--teal)' : '2px solid transparent',
                  background: isDropTarget ? 'rgba(20,80,74,0.14)' : active ? 'var(--teal)' : 'transparent',
                  color: isDropTarget ? 'var(--teal)' : active ? '#fff' : 'var(--gray-500)',
                  transform: isDropTarget ? 'scale(1.05)' : 'none',
                  transition: 'all 0.12s',
                }}>
                {t.label}{count != null ? ` (${count.toLocaleString()})` : ''}
              </button>
            );
          })}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
            <ViewToggle value={viewMode} onChange={setView} />
            <ListFilter value={listFilter} onChange={setListFilter} />
            <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
              Looking for new deals? → <span onClick={() => navigate('/mls-opportunities')} style={{ color: 'var(--teal)', cursor: 'pointer', textDecoration: 'underline' }}>MLS Opportunities</span>
            </span>
          </div>
        </div>

        {/* Selection bar — list-membership actions (shared) + mass delete */}
        <div style={{ padding: selectedIds.size > 0 ? '12px 20px 0' : 0 }}>
          <SelectionBar count={selectedIds.size} items={selectionItems} onClear={() => setSelectedIds(new Set())} />
          {selectedIds.size > 0 && (
            <div style={{ marginTop: 8 }}>
              <button onClick={handleBulkDelete}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid rgba(184,84,80,0.4)', background: 'rgba(184,84,80,0.12)', color: '#B85450', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                🗑 Delete {selectedIds.size} deal{selectedIds.size > 1 ? 's' : ''}
              </button>
            </div>
          )}
        </div>

        {/* Sheet-bridge (org 57): sync controls + "left the sheet" review banner, Available tab only. */}
        {sheetView && (
          <SheetSyncBar syncing={syncing} syncMsg={syncMsg} onSync={doSheetSync} canSync={canSheetSync}
            flagged={flaggedDeals} onMarkSold={setSellDeal} onMarkDropped={handleMarkDropped} />
        )}

        {/* List view — sheet-style table for org 57's Available tab, standard pipeline table otherwise. */}
        {viewMode === 'list' && sheetView ? (
          <SheetDealsTable deals={visibleDeals} loading={loading} navigate={navigate}
            onMarkSold={setSellDeal} onMarkDropped={handleMarkDropped} />
        ) : viewMode === 'list' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(250, 247, 242,0.6)', borderBottom: '1px solid var(--navy-border)' }}>
                {getColumns(isClosedTab).map(col => {
                  const sortKey = SORTABLE[col.key];
                  const on = sort && sort.key === sortKey;
                  return (
                    <th key={col.key} style={{ padding: col.key === 'pin' ? '10px 4px 10px 12px' : '10px 10px', fontSize: 10, fontWeight: 700, color: on ? 'var(--teal)' : 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'left', whiteSpace: 'nowrap' }}>
                      {col.key === 'pin'
                        ? <RowCheckbox checked={allVisibleSelected} onChange={toggleSelectAll} title="Select all" />
                        : sortKey ? (
                          <span onClick={() => onSort(sortKey)} title={`Sort by ${col.label}`}
                            style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {col.label}
                            {/* Arrow only on the active column — a caret on every header is noise, and
                                a greyed-out one reads as disabled rather than sortable. */}
                            <span style={{ fontSize: 9, opacity: on ? 1 : 0.28 }}>
                              {on ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                            </span>
                          </span>
                        ) : col.label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={17} style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading deals…</td></tr>
              ) : visibleDeals.length === 0 ? (
                <tr><td colSpan={17} style={{ padding: 60, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
                  {listFilter ? 'No deals in this tab match the selected list.' : (
                    <>No deals in this tab yet.<br/>
                    <span style={{ fontSize: 11 }}>Generate a contract on a listing in <span onClick={() => navigate('/mls-opportunities')} style={{ color: 'var(--teal)', cursor: 'pointer', textDecoration: 'underline' }}>MLS Opportunities</span> to start a new deal.</span></>
                  )}
                </td></tr>
              ) : visibleDeals.map((deal, i) => (
                <PipelineRow key={deal.id} deal={deal} idx={i} total={visibleDeals.length} isClosedTab={isClosedTab} navigate={navigate}
                  selected={selectedIds.has(deal.id)} onToggleSelect={toggleSelect}
                  onOpenMarket={setMarketDeal} onOpenSell={setSellDeal}
                  orgUsers={orgUsers} onReassign={handleReassign} onDelete={handleDelete} onMarkAvailable={handleMarkAvailable}
                  onInlineSave={handleInlineSave} onPin={handlePin}
                  dragging={draggingId === deal.id}
                  onDragStartRow={() => setDraggingId(deal.id)}
                  onDragEndRow={() => { setDraggingId(null); setDragOverTab(null); }}
                  onStageChanged={() => { fetchDeals(activeTab); refreshCounts(); }} />
              ))}
            </tbody>
          </table>
        </div>
        )}

        {/* Map view */}
        {viewMode === 'map' && (
          <div style={{ padding: 16 }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading deals…</div>
            ) : (
              <DealsMap deals={visibleDeals} selectedId={selectedDealId}
                onSelect={(d) => { setSelectedDealId(d.id); setView('detail'); }} />
            )}
          </div>
        )}

        {/* Detail view — master list + detail pane */}
        {viewMode === 'detail' && (
          <DealsDetailView
            deals={visibleDeals} loading={loading} selectedId={selectedDealId} onSelectId={setSelectedDealId}
            navigate={navigate} orgUsers={orgUsers}
            onInlineSave={handleInlineSave} onPin={handlePin} onDelete={handleDelete}
            onStageChanged={() => { fetchDeals(activeTab); refreshCounts(); }} />
        )}

        {viewMode === 'list' && deals.length === 500 && (
          <div style={{ padding: '10px 20px', fontSize: 11, color: 'var(--gray-500)', borderTop: '1px solid var(--navy-border)', textAlign: 'center' }}>
            Showing first 500 records — refine by status to see more.
          </div>
        )}
      </div>

      {/* "Market This Deal" wizard — opened from any Under Contract row's row action */}
      {marketDeal && (
        <MarketDealModal
          deal={marketDeal}
          onClose={() => setMarketDeal(null)}
          onSent={() => { /* table reloads on tab switch; nothing live to refresh here */ }}
        />
      )}

      {/* One-click "Mark as Sold" from an Available row — captures sold price + close date, then
          moves the deal straight to Sold (backend gates to the owner / org admin). */}
      {sellDeal && (
        <SoldCloseModal
          dealId={sellDeal.id}
          stage="Sold"
          deal={sellDeal}
          onClose={() => setSellDeal(null)}
          onConfirmed={() => { setSellDeal(null); reload(); }}
        />
      )}

      {/* Drag-and-drop onto the Sold / Closed bucket — confirm sold price + close date first. */}
      {publishDeal && (
        <MarkAvailableModal
          dealId={publishDeal.id}
          address={publishDeal.address}
          onClose={() => setPublishDeal(null)}
          onPublished={() => { setPublishDeal(null); reload(); }}
        />
      )}

      {stageModal && (
        <SoldCloseModal
          dealId={stageModal.deal.id}
          stage={stageModal.stage}
          deal={stageModal.deal}
          onClose={() => setStageModal(null)}
          onConfirmed={() => { setStageModal(null); reload(); }}
        />
      )}
    </div>
  );
}

function getColumns(isClosedTab) {
  const base = [
    { key: 'pin',     label: '' },     // pin-to-top control
    { key: 'photo',   label: '' },
    { key: 'addr',    label: 'Property' },
    // Year built sits next to the address, before the money: it is a property fact, and an agent
    // scanning the board for rehab candidates reads it at the same moment as the address.
    { key: 'yearbuilt', label: 'Year Built' },
    { key: 'list',    label: 'List Price' },
    { key: 'arv',     label: 'ARV' },
    { key: 'offer',   label: 'Offer' },   // MAO until an offer is entered, then the offer amount
    { key: 'agent',   label: 'Agent' },
    { key: 'offerdt', label: 'Offer Date' },
    { key: 'liststat',label: 'Listing Status' },
    { key: 'offstat', label: 'Offer Status' },
    { key: 'followup',label: 'Follow-up' },
  ];
  if (isClosedTab) {
    base.push({ key: 'closed', label: 'Closed' });
    base.push({ key: 'title', label: 'Title Company' });
  }
  base.push({ key: 'notes',   label: '' }); // notes popover trigger
  base.push({ key: 'actions', label: '' }); // row-level action kebab
  return base;
}

// ── Sheet-bridge (org 57) UI ─────────────────────────────────────────────────────────────────────
const AMBER_BG = 'rgba(214,158,46,0.12)', AMBER_BORDER = 'rgba(214,158,46,0.45)', AMBER_TEXT = '#B7860B';

// ── Deal dashboard ────────────────────────────────────────────────────────────
// The summary the Deals page opens on. Four buckets, one period toggle, and the owner's
// Mine/Everyone switch. Cards are buttons: clicking one jumps to that bucket's tab, so the numbers
// are a way into the work rather than a readout you then have to go find.
//
// Counts come from GET /api/deals/dashboard, which applies the SAME scope and period as the list
// below it — if these two ever disagree, the cards are advertising rows the table won't show.
const PERIOD_OPTIONS = [
  ['current-month', 'This month'],
  ['next-month',    'Next month'],
  ['last-month',    'Last month'],
  ['ytd',           'Year to date'],
  ['all',           'All time'],
];

// bucket key → the tab it opens. 'sold' covers Sold + Closed, so it lands on Sold.
const CARD_TAB = { offers_sent: 'offer-sent', sold: 'sold', dropped: 'dropped', available: 'available' };
const CARDS = [
  ['offers_sent', 'Offers Sent', 'var(--gold)'],
  ['sold',        'Sold',        'var(--teal)'],
  ['dropped',     'Dropped',     'var(--gray-500)'],
  ['available',   'Available',   '#5B8DB8'],
];

function DealDashboard({ summary, period, onPeriod, activeTab, onPick, scope, onScope, canScope }) {
  const pill = (on) => ({
    padding: '5px 13px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600,
    border: '1px solid ' + (on ? 'var(--teal)' : 'var(--navy-border)'),
    background: on ? 'var(--teal)' : 'transparent',
    color: on ? '#fff' : 'var(--gray-500)',
  });
  const n = (k) => (summary && typeof summary[k] === 'number' ? summary[k].toLocaleString() : '—');

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 2 }}>Period</span>
        {PERIOD_OPTIONS.map(([key, label]) => (
          <button key={key} onClick={() => onPeriod(key)} style={pill(period === key)}>{label}</button>
        ))}

        {/* Mine ⇄ Everyone — only for a member who actually has both views. An agent never sees it,
            and the backend ignores ?scope=all from them regardless. */}
        {canScope && (
          <>
            <span style={{ width: 1, height: 20, background: 'var(--navy-border)', margin: '0 6px' }} />
            {[['mine', 'Mine'], ['all', 'Everyone']].map(([key, label]) => (
              <button key={key} onClick={() => onScope(key)} style={pill(scope === key)}>{label}</button>
            ))}
          </>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
        {CARDS.map(([key, label, color]) => {
          const on = activeTab === CARD_TAB[key];
          return (
            <button key={key} onClick={() => onPick(CARD_TAB[key])}
              title={`Show ${label}`}
              style={{
                textAlign: 'left', cursor: 'pointer', padding: '14px 16px', borderRadius: 12,
                background: 'var(--navy-card)',
                border: '1px solid ' + (on ? 'var(--teal)' : 'var(--navy-border)'),
                boxShadow: on ? '0 0 0 1px var(--teal) inset' : 'none',
              }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                {label}
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1 }}>{n(key)}</div>
            </button>
          );
        })}
      </div>

      {summary && summary.fees > 0 && (
        <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 10 }}>
          {' '}Assignment fees on sold/closed in this period:{' '}
          <span style={{ color: 'var(--gold)', fontWeight: 700 }}>
            ${summary.fees.toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
}

// Sync controls + "left the sheet" review banner. Shown above the Available list for org 57 only.
// canSync: only an org owner/admin gets the manual "Sync now" control. The endpoint already refuses
// anyone else (routes/deals.js POST /sync-sheet), so this is about not offering agents a button that
// spends the org's Google Sheet + MLS budget — and not showing them one that only ever errors.
// The bar itself still renders for agents: the hourly schedule and the review banner are useful to
// everyone, it's only the manual trigger that's restricted.
function SheetSyncBar({ syncing, syncMsg, onSync, flagged, onMarkSold, onMarkDropped, canSync }) {
  return (
    <div style={{ padding: '12px 20px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
          🔗 Mirrored from your Available and Closings Google Sheets · syncs hourly (9am–6pm AZ)
        </span>
        {canSync && (
          <button onClick={onSync} disabled={syncing}
            style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid var(--teal)', background: syncing ? 'transparent' : 'var(--teal)', color: syncing ? 'var(--teal)' : '#fff', fontSize: 12, fontWeight: 600, cursor: syncing ? 'default' : 'pointer' }}>
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
        )}
        {syncMsg && <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{syncMsg}</span>}
      </div>

      {flagged.length > 0 && (
        <div style={{ marginTop: 10, border: `1px solid ${AMBER_BORDER}`, background: AMBER_BG, borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: AMBER_TEXT, marginBottom: 8 }}>
            ⚠ {flagged.length} deal{flagged.length > 1 ? 's' : ''} left your sheet
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 8 }}>
            Nothing to do: a deal that shows up on the Closings sheet moves to Sold automatically, and one that
            doesn't within 24 hours is marked Dropped. Use the buttons only to settle one sooner.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {flagged.map(d => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: 'var(--white)', flex: '1 1 240px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.address || '(no address)'}</span>
                <button onClick={() => onMarkSold(d)}
                  style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid var(--teal)', background: 'var(--teal)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Sold</button>
                <button onClick={() => onMarkDropped(d.id)}
                  style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-300)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Dropped</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// The sheet's own columns (Address / Price / Summary / ARV / COE / Picture Link) for org 57's
// Available tab. ARV + COE render verbatim from the sheet (arv_text/close_of_escrow); Price uses the
// house money format. Rows flagged as removed get an amber marker + inline triage.
function SheetDealsTable({ deals, loading, navigate, onMarkSold, onMarkDropped }) {
  const th = { padding: '10px 10px', fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'left', whiteSpace: 'nowrap' };
  const td = { padding: '10px 10px', fontSize: 13, color: 'var(--white)', verticalAlign: 'top' };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'rgba(250, 247, 242,0.6)', borderBottom: '1px solid var(--navy-border)' }}>
            <th style={th}>Address</th><th style={th}>Price</th><th style={th}>Summary</th>
            <th style={th}>ARV</th><th style={th}>COE</th><th style={th}>Picture Link</th><th style={th}></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading deals…</td></tr>
          ) : deals.length === 0 ? (
            <tr><td colSpan={7} style={{ padding: 60, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
              No available deals yet. They appear here after the next sync from the Google Sheet.
            </td></tr>
          ) : deals.map(d => {
            const removed = !!d.sheet_removed_at;
            return (
              <tr key={d.id}
                onClick={() => navigate(`/underwriting/${d.id}`)}
                style={{ borderBottom: '1px solid var(--navy-border)', cursor: 'pointer', background: removed ? AMBER_BG : 'transparent', borderLeft: removed ? `3px solid ${AMBER_BORDER}` : '3px solid transparent' }}>
                <td style={{ ...td, fontWeight: 600, maxWidth: 260 }}>
                  {d.address || '—'}
                  {removed && <div style={{ fontSize: 11, color: AMBER_TEXT, fontWeight: 700, marginTop: 3 }}>⚠ left the sheet</div>}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtMoney(d.asking_price)}</td>
                <td style={{ ...td, maxWidth: 360, color: 'var(--gray-300)', whiteSpace: 'normal' }}>{d.description || '—'}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{d.arv_text || (d.ai_arv ? fmtMoney(d.ai_arv) : '—')}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{d.close_of_escrow || '—'}</td>
                <td style={td} onClick={e => e.stopPropagation()}>
                  {d.photos_link
                    ? <a href={d.photos_link} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)', fontSize: 12, textDecoration: 'underline' }}>View photos ↗</a>
                    : <span style={{ color: 'var(--gray-500)' }}>—</span>}
                </td>
                <td style={td} onClick={e => e.stopPropagation()}>
                  {removed && (
                    <div style={{ display: 'flex', gap: 6, whiteSpace: 'nowrap' }}>
                      <button onClick={() => onMarkSold(d)}
                        style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--teal)', background: 'var(--teal)', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Sold</button>
                      <button onClick={() => onMarkDropped(d.id)}
                        style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-300)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Dropped</button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PipelineRow({ deal, idx, total, isClosedTab, navigate, selected, onToggleSelect, onOpenMarket, onOpenSell, onStageChanged, orgUsers, onReassign, onDelete, onMarkAvailable, onInlineSave, onPin, dragging, onDragStartRow, onDragEndRow }) {
  const last = idx === total - 1;
  // Marketing button is only useful once the deal is in our hands to assign.
  // Outside Under Contract the button would just trigger blasts on deals we can't actually deliver.
  const canMarket = deal.stage === 'Under Contract';
  const save = (patch) => onInlineSave(deal.id, patch);
  const offerMade = deal.offer_amount != null && deal.offer_amount !== '';
  const stopClick = e => e.stopPropagation();

  return (
    <tr
      draggable
      onDragStart={e => {
        // Don't start a row drag when the gesture begins on an interactive control (inline editors,
        // notes/kebab buttons, links) — those need normal click/drag-to-select behavior.
        if (e.target.closest('input, select, textarea, button, a')) { e.preventDefault(); return; }
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(deal.id));
        onDragStartRow();
      }}
      onDragEnd={() => onDragEndRow()}
      onClick={() => { if (!dragging) navigate(`/deals/${deal.id}`); }}
      style={{
        borderBottom: last ? 'none' : '1px solid rgba(232, 227, 218, 0.7)',
        cursor: 'grab', transition: 'background 0.12s',
        opacity: dragging ? 0.45 : 1,
        background: selected ? 'rgba(20, 80, 74,0.06)' : deal.pinned ? 'rgba(224,165,38,0.05)' : 'transparent',
      }}
      onMouseOver={e => { if (!dragging) e.currentTarget.style.background = selected ? 'rgba(20, 80, 74,0.10)' : 'rgba(20, 80, 74,0.04)'; }}
      onMouseOut={e => e.currentTarget.style.background = selected ? 'rgba(20, 80, 74,0.06)' : deal.pinned ? 'rgba(224,165,38,0.05)' : 'transparent'}
    >
      {/* Pin + select clustered on the far left */}
      <td style={{ padding: '10px 4px 10px 12px', width: 30 }} onClick={stopClick}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <PinStar pinned={!!deal.pinned} onToggle={() => onPin(deal.id, !deal.pinned)} />
          <RowCheckbox checked={!!selected} onChange={(on) => onToggleSelect(deal.id, on)} />
        </div>
      </td>
      <td style={{ padding: '10px 12px', width: 60 }}>
        {deal.photo_url ? (
          <img src={proxyPhoto(deal.photo_url)} alt="" style={{ width: 50, height: 38, borderRadius: 6, objectFit: 'cover', border: '1px solid var(--navy-border)' }} />
        ) : (
          <div style={{ width: 50, height: 38, borderRadius: 6, background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--navy-border)' }}>
            <HomeIconSmall />
          </div>
        )}
      </td>
      <td style={{ padding: '10px 16px', minWidth: 180 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--white)', fontWeight: 600 }}>{deal.address}</span>
          <ListDots type="deal" id={deal.id} />
        </div>
        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{[deal.city, deal.state, deal.zip].filter(Boolean).join(', ')}</div>
      </td>
      {/* Year built — inline editable; it gates publishing to the shared board, so it is fillable
          from the row rather than only from the Edit Deal modal. */}
      <td style={{ padding: '10px 10px' }} onClick={stopClick}>
        <InlineYear value={deal.year_built} onSave={v => save({ year_built: v })} />
      </td>
      {/* List price — static (comes from the listing) */}
      <td style={{ padding: '10px 10px', fontSize: 13, color: 'var(--gray-300)', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtMoney(deal.asking_price)}</td>
      {/* ARV — inline editable; grey/translucent when app-estimated, bold white when user-verified */}
      <td style={{ padding: '10px 10px' }} onClick={stopClick}>
        <InlineMoney value={deal.ai_arv} onSave={v => save({ ai_arv: v })} source={deal.arv_source} verifiedColor="var(--white)" />
      </td>
      {/* Offer — MAO (faded) until an offer is entered, then the offer amount (teal, bold) */}
      <td style={{ padding: '10px 10px' }} onClick={stopClick}>
        {offerMade ? (
          <InlineMoney value={deal.offer_amount} onSave={v => save({ offer_amount: v })} source="manual" verifiedColor="var(--teal)" tag="OFFER" />
        ) : (
          <InlineMoney value={null} onSave={v => save({ offer_amount: v })} source="estimated"
            hint={deal.max_offer != null ? `MAO ${fmtMoney(deal.max_offer)}` : 'Set offer'} />
        )}
      </td>
      {/* Agent — email truncates so the column stays narrow */}
      <td style={{ padding: '10px 10px' }}>
        {deal.agent_name || deal.agent_phone || deal.agent_email ? (
          <div style={{ lineHeight: 1.35, maxWidth: 160 }}>
            {deal.agent_name && <div style={{ fontSize: 12, color: 'var(--white)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{deal.agent_name}</div>}
            {deal.agent_phone && <div style={{ fontSize: 11, color: 'var(--gray-300)' }}>{deal.agent_phone}</div>}
            {deal.agent_email && <div style={{ fontSize: 11, color: 'var(--gray-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{deal.agent_email}</div>}
          </div>
        ) : <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>—</span>}
      </td>
      {/* Offer date — inline date picker */}
      <td style={{ padding: '10px 10px' }} onClick={stopClick}>
        <InlineDate value={deal.offer_date} onSave={v => save({ offer_date: v })} />
      </td>
      {/* Listing status — auto from MLS when linked, else editable */}
      <td style={{ padding: '10px 10px' }} onClick={stopClick}>
        <ListingStatusCell deal={deal} onSave={v => save({ listing_status: v })} />
      </td>
      {/* Offer status — inline dropdown */}
      <td style={{ padding: '10px 10px' }} onClick={stopClick}>
        <InlineSelect value={deal.offer_status} options={OFFER_STATUS_OPTIONS} onSave={v => save({ offer_status: v })} placeholder="—" tone="offer" />
      </td>
      {/* Follow-up — next scheduled, else last sent */}
      <td style={{ padding: '10px 10px' }}>
        <FollowUpBadge next={deal.next_followup_at} last={deal.last_followup_at} />
      </td>
      {isClosedTab && <td style={{ padding: '10px 10px', fontSize: 12, color: 'var(--gray-500)' }}>{fmtDate(deal.closed_date)}</td>}
      {isClosedTab && <td style={{ padding: '10px 10px', fontSize: 12, color: 'var(--gray-300)', whiteSpace: 'nowrap' }}>{deal.title_company || '—'}</td>}
      {/* Notes popover */}
      <td style={{ padding: '10px 6px', width: 34 }} onClick={stopClick}>
        <NotesPopover dealId={deal.id} count={Number(deal.notes_count) || 0} />
      </td>
      {/* Row actions — compact kebab (stage move, owner, mark available/sold, market, delete) */}
      <td style={{ padding: '10px 8px 10px 6px', width: 34 }} onClick={stopClick}>
        <RowActions deal={deal} orgUsers={orgUsers} onReassign={onReassign} onDelete={onDelete}
          onMarkAvailable={onMarkAvailable} onOpenSell={onOpenSell} onOpenMarket={onOpenMarket}
          canMarket={canMarket} onStageChanged={onStageChanged} />
      </td>
    </tr>
  );
}

// List / Map / Detail segmented toggle (mirrors the MLS Opportunities view switch).
function ViewToggle({ value, onChange }) {
  const opts = [
    { key: 'list',   label: 'List' },
    { key: 'map',    label: 'Map' },
    { key: 'detail', label: 'Detail' },
  ];
  return (
    <div style={{ display: 'flex', background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 8, overflow: 'hidden' }}>
      {opts.map(o => (
        <button key={o.key} onClick={() => onChange(o.key)}
          style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer',
            background: value === o.key ? 'var(--teal)' : 'transparent', color: value === o.key ? '#fff' : 'var(--gray-300)' }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Detail view — a master list (left) + a detail pane (right) for the selected deal, matching the
// MLS Opportunities detail layout. The list is scoped to the deals visible in the current bucket.
function DealsDetailView({ deals, loading, selectedId, onSelectId, navigate, orgUsers, onInlineSave, onPin, onDelete, onStageChanged }) {
  const selected = deals.find(d => d.id === selectedId) || null;
  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading deals…</div>;
  if (!deals.length) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>No deals in this tab yet.</div>;
  return (
    <div style={{ display: 'flex', minHeight: 520 }}>
      {/* Master list */}
      <div style={{ width: 300, flexShrink: 0, borderRight: '1px solid var(--navy-border)', maxHeight: 640, overflowY: 'auto' }}>
        {deals.map(d => {
          const active = d.id === selectedId;
          const offer = d.offer_amount != null ? `Offer ${fmtMoney(d.offer_amount)}` : (d.max_offer != null ? `MAO ${fmtMoney(d.max_offer)}` : '');
          return (
            <div key={d.id} onClick={() => onSelectId(d.id)}
              style={{ padding: '12px 16px', borderBottom: '1px solid rgba(232,227,218,0.5)', cursor: 'pointer',
                background: active ? 'rgba(20,80,74,0.10)' : d.pinned ? 'rgba(224,165,38,0.05)' : 'transparent',
                borderLeft: active ? '3px solid var(--teal)' : '3px solid transparent' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {!!d.pinned && <span style={{ color: 'var(--gold)', fontSize: 11 }}>★</span>}
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>{d.address}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{[d.city, d.state, d.zip].filter(Boolean).join(', ')}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--gray-300)' }}>ARV {fmtMoney(d.ai_arv)}</span>
                {offer && <span style={{ fontSize: 11, color: 'var(--teal)', fontWeight: 600 }}>· {offer}</span>}
              </div>
            </div>
          );
        })}
      </div>
      {/* Detail pane */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {selected ? (
          <DealDetailPane deal={selected} navigate={navigate} orgUsers={orgUsers}
            onInlineSave={onInlineSave} onPin={onPin} onDelete={onDelete} onStageChanged={onStageChanged} />
        ) : (
          <div style={{ padding: 40, color: 'var(--gray-500)', fontSize: 13 }}>Select a deal to see details.</div>
        )}
      </div>
    </div>
  );
}

// Full-ish detail pane for one deal (in the Detail view). Reuses the inline editors so the same
// fields are editable here as in the list. "Open deal →" links to the underwriting page for now
// (the dedicated /deals/:id page is a later increment).
function DealDetailPane({ deal, navigate, onInlineSave, onPin, onStageChanged }) {
  const save = (patch) => onInlineSave(deal.id, patch);
  const offerMade = deal.offer_amount != null && deal.offer_amount !== '';
  const Field = ({ label, children }) => (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
      <div>{children}</div>
    </div>
  );
  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 20 }}>
        {deal.photo_url ? (
          <img src={proxyPhoto(deal.photo_url)} alt="" style={{ width: 120, height: 88, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--navy-border)' }} />
        ) : (
          <div style={{ width: 120, height: 88, borderRadius: 8, background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--navy-border)' }}><HomeIconSmall /></div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <PinStar pinned={!!deal.pinned} onToggle={() => onPin(deal.id, !deal.pinned)} />
            <h2 style={{ fontSize: 19, fontWeight: 700, color: 'var(--white)' }}>{deal.address}</h2>
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 8 }}>{[deal.city, deal.state, deal.zip].filter(Boolean).join(', ')}</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <StageBadge stage={deal.stage} dealId={deal.id} deal={deal} onChanged={onStageChanged} />
            <button onClick={() => navigate(`/deals/${deal.id}`)}
              style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid var(--teal)', background: 'transparent', color: 'var(--teal)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Open deal →
            </button>
          </div>
        </div>
      </div>

      {/* Metrics + status grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, marginBottom: 20 }}>
        <Field label="List Price"><span style={{ fontSize: 15, fontWeight: 600, color: 'var(--gray-300)' }}>{fmtMoney(deal.asking_price)}</span></Field>
        <Field label="ARV"><InlineMoney value={deal.ai_arv} onSave={v => save({ ai_arv: v })} source={deal.arv_source} verifiedColor="var(--white)" /></Field>
        <Field label={offerMade ? 'Offer Amount' : 'MAO → set offer'}>
          {offerMade
            ? <InlineMoney value={deal.offer_amount} onSave={v => save({ offer_amount: v })} source="manual" verifiedColor="var(--teal)" tag="OFFER" />
            : <InlineMoney value={null} onSave={v => save({ offer_amount: v })} source="estimated" hint={deal.max_offer != null ? `MAO ${fmtMoney(deal.max_offer)}` : 'Set offer'} />}
        </Field>
        <Field label="Offer Date"><InlineDate value={deal.offer_date} onSave={v => save({ offer_date: v })} /></Field>
        <Field label="Offer Status"><InlineSelect value={deal.offer_status} options={OFFER_STATUS_OPTIONS} onSave={v => save({ offer_status: v })} placeholder="—" tone="offer" /></Field>
        <Field label="Listing Status"><ListingStatusCell deal={deal} onSave={v => save({ listing_status: v })} /></Field>
        <Field label="Follow-up"><FollowUpBadge next={deal.next_followup_at} last={deal.last_followup_at} /></Field>
        <Field label="Beds / Baths / Sqft"><span style={{ fontSize: 13, color: 'var(--gray-300)' }}>{[deal.beds && `${deal.beds}bd`, deal.baths && `${deal.baths}ba`, deal.sqft && `${deal.sqft.toLocaleString()} sqft`].filter(Boolean).join(' / ') || '—'}</span></Field>
      </div>

      {/* Agent */}
      {(deal.agent_name || deal.agent_phone || deal.agent_email) && (
        <div style={{ marginBottom: 20 }}>
          <Field label="Listing Agent">
            <div style={{ fontSize: 13, color: 'var(--white)', fontWeight: 600 }}>{deal.agent_name}</div>
            <div style={{ fontSize: 12, color: 'var(--gray-300)' }}>{[deal.agent_phone, deal.agent_email].filter(Boolean).join(' · ')}</div>
          </Field>
        </div>
      )}

      {deal.description && (
        <div style={{ marginBottom: 20 }}>
          <Field label="Description"><div style={{ fontSize: 13, color: 'var(--gray-200)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{deal.description}</div></Field>
        </div>
      )}

      {/* Notes thread */}
      <div style={{ borderTop: '1px solid var(--navy-border)', paddingTop: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Notes</div>
        <NotesThread dealId={deal.id} />
      </div>
    </div>
  );
}

// Always-open notes thread (used in the detail pane). Same endpoints as NotesPopover.
export function NotesThread({ dealId }) {
  const [notes, setNotes] = React.useState(null);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    setNotes(null);
    fetch(`/api/deals/${dealId}/notes`).then(r => r.json())
      .then(d => { if (alive) setNotes(Array.isArray(d) ? d : []); }).catch(() => { if (alive) setNotes([]); });
    return () => { alive = false; };
  }, [dealId]);

  const add = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/deals/${dealId}/notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body }),
      });
      const note = await r.json();
      if (!r.ok || note.error) throw new Error(note.error || 'failed');
      setNotes(prev => [...(prev || []), note]);
      setDraft('');
    } catch (e) { alert(`Add note failed: ${e.message}`); }
    finally { setBusy(false); }
  };
  const fmtStamp = (s) => { const d = new Date(s); return isNaN(d) ? String(s) : d.toLocaleString([], { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); };

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
        {notes == null ? <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading…</div>
          : notes.length === 0 ? <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>No notes yet.</div>
          : notes.map(n => (
            <div key={n.id} style={{ borderLeft: '2px solid var(--navy-border)', paddingLeft: 10 }}>
              <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{fmtStamp(n.created_at)}{n.author ? ` · ${n.author}` : ''}</div>
              <div style={{ fontSize: 13, color: 'var(--gray-200)', whiteSpace: 'pre-wrap' }}>{n.body}</div>
            </div>
          ))}
      </div>
      <textarea value={draft} onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); add(); } }}
        placeholder="Add a note — call notes, buyer feedback, next steps… (Enter to add, Shift+Enter for a line break)"
        rows={2}
        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', resize: 'vertical' }} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button onClick={add} disabled={busy || !draft.trim()}
          style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: busy || !draft.trim() ? 'default' : 'pointer', opacity: busy || !draft.trim() ? 0.5 : 1 }}>
          {busy ? 'Adding…' : 'Add note'}
        </button>
      </div>
    </div>
  );
}

// Owner reassignment control — shows the deal's current owner; click to reassign to another member
// of the org. Hits PUT /api/deals/:id/owner. With one user in the org today the menu lists just them.
// Compact row-actions kebab. Collapses the per-row controls (stage move, owner reassign, mark
// available/sold, market, delete) into one narrow column so the table fits without horizontal scroll.
// The menu is portalled so it (and the nested stage/owner dropdowns) are never clipped.
function RowActions({ deal, orgUsers, onReassign, onDelete, onMarkAvailable, onOpenSell, onOpenMarket, canMarket, onStageChanged }) {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const rect = useAnchorRect(open, btnRef, 230);

  React.useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const Item = ({ label, onClick, color }) => (
    <button onClick={() => { setOpen(false); onClick(); }}
      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: color || 'var(--gray-200)', borderRadius: 6 }}
      onMouseOver={e => e.currentTarget.style.background = 'var(--navy)'}
      onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
      {label}
    </button>
  );

  return (
    <>
      <button ref={btnRef} onClick={() => setOpen(o => !o)} title="Actions"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: 4, display: 'inline-flex', borderRadius: 6 }}>
        <KebabIcon />
      </button>
      {open && (
        <PortalPanel rect={rect} panelRef={panelRef} pad={6}>
          <div style={{ padding: '4px 10px 8px', borderBottom: '1px solid var(--navy-border)', marginBottom: 4 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Stage</div>
            <StageBadge stage={deal.stage} dealId={deal.id} deal={deal} onChanged={() => { onStageChanged && onStageChanged(); }} />
          </div>
          <div style={{ padding: '2px 10px 6px', borderBottom: '1px solid var(--navy-border)', marginBottom: 4 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Owner</div>
            <OwnerCell deal={deal} orgUsers={orgUsers} onReassign={onReassign} />
          </div>
          {deal.stage === 'Available'
            ? <Item label="✓ Mark as Sold" color="#4A7C59" onClick={() => onOpenSell && onOpenSell(deal)} />
            : <Item label="↑ Mark Available" color="#4A7C59" onClick={() => onMarkAvailable(deal.id)} />}
          {canMarket && <Item label="📣 Market to buyers" color="#FF7F50" onClick={() => onOpenMarket && onOpenMarket(deal)} />}
          <Item label="🗑 Delete deal" color="#B85450" onClick={() => onDelete(deal.id)} />
        </PortalPanel>
      )}
    </>
  );
}

function KebabIcon() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="12" cy="19" r="1.6"/></svg>; }

function OwnerCell({ deal, orgUsers, onReassign }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const owner = orgUsers.find(u => u.id === deal.owner_user_id);
  const label = owner ? (owner.name || owner.email) : 'Unassigned';
  const short = label.length > 16 ? label.slice(0, 15) + '…' : label;

  return (
    <span ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} title={`Owner: ${label} — click to reassign`}
        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'transparent', color: 'var(--gray-300)', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
        <UserIconSmall /> {short} <span style={{ fontSize: 8, opacity: 0.7 }}>▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 8, padding: 4, boxShadow: '0 6px 20px rgba(26,23,20,0.12)', width: 220 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '4px 8px 6px' }}>Reassign owner…</div>
          {orgUsers.length === 0 && <div style={{ padding: '6px 8px', fontSize: 11, color: 'var(--gray-500)' }}>No org members loaded.</div>}
          {orgUsers.map(u => {
            const active = u.id === deal.owner_user_id;
            return (
              <div key={u.id} onClick={() => { if (!active) onReassign(deal.id, u.id); setOpen(false); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, cursor: active ? 'default' : 'pointer', background: active ? 'var(--navy)' : 'transparent' }}
                onMouseOver={e => !active && (e.currentTarget.style.background = 'var(--navy)')}
                onMouseOut={e => !active && (e.currentTarget.style.background = 'transparent')}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--white)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name || u.email}</div>
                  {u.role && <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{u.role}</div>}
                </span>
                {active && <span style={{ fontSize: 9, color: 'var(--gray-500)' }}>current</span>}
              </div>
            );
          })}
        </div>
      )}
    </span>
  );
}

function UserIconSmall()  { return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>; }
function HomeIconSmall()  { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(156, 150, 144,0.5)" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>; }
